import React, { FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { base_url } from "constant/constant";
import PropertyCardEuropea from "components/PropertyCardEuropea/PropertyCardEuropea";
import { useWishlist } from "WishlistContext";
import emptyStateIcon from "images/card/empty-state-icon.svg";
import { useLocale } from "i18n/locale";

export interface WishlistPageProps {
  className?: string;
}

const WishlistPage: FC<WishlistPageProps> = ({ className = "" }) => {
  const { t } = useLocale();
  const { savedIds, mergeSaved } = useWishlist();
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/wishlist/list_wishlist`);
      if (!res.data?.error && Array.isArray(res.data?.data)) {
        // Shape each property like the listing grid expects: galleryImgs first.
        const mapped = res.data.data.map((property: any) => ({
          ...property,
          galleryImgs: property.images || [],
        }));
        setProperties(mapped);
        // Keep the heart/saved state consistent with what we're showing.
        mergeSaved(mapped.map((p: any) => String(p._id)));
      } else {
        setProperties([]);
      }
    } catch (error) {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, []);

  // Reflect un-saves done from a card's heart without a full refetch: only keep
  // properties that are still in the saved set.
  const visibleProperties = useMemo(
    () => properties.filter((p) => savedIds.includes(String(p._id))),
    [properties, savedIds]
  );

  // Group by city so each destination gets its own titled section (Paris, Brussels…).
  const groups = useMemo(() => {
    const map = new Map<string, any[]>();
    visibleProperties.forEach((p) => {
      const raw = (p.location || p.city || "Other").toString();
      const city = raw.charAt(0).toUpperCase() + raw.slice(1);
      if (!map.has(city)) map.set(city, []);
      map.get(city)!.push(p);
    });
    return Array.from(map.entries());
  }, [visibleProperties]);

  return (
    <div className={`nc-WishlistPage ${className}`}>
      <Helmet>
        <title>Wishlist | Europea</title>
      </Helmet>

      {/* Teal sub-header — mirrors the Legal page header exactly */}
      <div
        className="w-full flex items-center h-[72px] sm:h-[90px] lg:h-[110px] px-4 sm:px-8 lg:px-[120px]"
        style={{ backgroundColor: "#01152E" }}
      >
        <h1
          className="text-[22px] sm:text-[28px] lg:text-[32px] leading-[28px] sm:leading-[36px] lg:leading-[40px] whitespace-nowrap"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            color: "#ffffff",
            margin: 0,
          }}
        >
          {t("wishlist.title")}
        </h1>
      </div>

      {/* Content section — full-width with the same 120px gutter as the Legal page */}
      <div
        className="w-full px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px]"
        style={{ backgroundColor: "#ffffff" }}
      >
        {loading ? (
          <div className="py-20 text-center text-[#445162]">{t("common.loading")}</div>
        ) : visibleProperties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <img
              src={emptyStateIcon}
              alt=""
              className="w-20 h-20 opacity-70 mb-6"
            />
            <h2
              className="text-[20px] text-[#01152E] mb-2"
              style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
            >
              {t("wishlist.empty")}
            </h2>
            <p
              className="text-[14px] text-[#445162] max-w-md"
              style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
            >
              {t("wishlist.emptyHint")}
            </p>
          </div>
        ) : (
          <div className="space-y-8 sm:space-y-12">
            {groups.map(([city, items]) => (
              <section key={city}>
                <h2
                  className="text-[24px] sm:text-[30px] lg:text-[36px] leading-[32px] sm:leading-[40px] lg:leading-[50px] mb-[16px] sm:mb-[24px]"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    color: "#01152E",
                    letterSpacing: "0.72px",
                  }}
                >
                  {city}
                </h2>
                <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item: any) => (
                    <PropertyCardEuropea
                      key={item._id}
                      id={item._id}
                      data={item}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
